<template>
  <v-container fill-height fluid class="primary custom-container-height">
    <v-col class="custom-height" xs="12" sm="12" md="12">
      <v-row wrap>
        <v-col xs="10" offset-xs="1" sm="10" offset-sm="1" md="8" offset-md="2">
          <div class="card-style rounded-card">
            <v-card-title class="title-style">
              <h4>Security Password Reset Required</h4>
            </v-card-title>
            <v-card-subtitle class="pb-0">
              For your security, a password reset is required to continue. Please verify your current password and
              create a new secure password.
            </v-card-subtitle>
            <v-divider class="mt-3"></v-divider>
            <v-card-text class="pa-4">
              <v-form ref="resetForm" v-model="valid">
                <v-text-field ref="username" v-model="usernameOrEmail" label="Username or Email" required dense outlined
                  prepend-inner-icon="account_circle" :rules="usernameRules" :disabled="!!prefilledUsername">
                </v-text-field>

                <v-text-field ref="currentPassword" v-model="currentPassword"
                  :type="showCurrentPassword ? 'text' : 'password'" label="Current Password" required dense outlined
                  prepend-inner-icon="lock" :append-icon="showCurrentPassword ? 'visibility' : 'visibility_off'"
                  :rules="currentPasswordRules" @click:append="showCurrentPassword = !showCurrentPassword">
                </v-text-field>

                <v-text-field ref="newPassword" v-model="newPassword" :type="showNewPassword ? 'text' : 'password'"
                  label="New Secure Password" required dense outlined prepend-inner-icon="lock_outline"
                  :append-icon="showNewPassword ? 'visibility' : 'visibility_off'"
                  :hide-details="!showPasswordValidation" @input="validatePassword"
                  @focus="showPasswordValidation = true" @blur="showPasswordValidation = newPassword.length > 0"
                  @click:append="showNewPassword = !showNewPassword">
                </v-text-field>

                <!-- Password Strength Indicator -->
                <div v-if="showPasswordValidation && newPassword.length > 0" class="mb-3">
                  <div class="d-flex align-center mb-1">
                    <span class="text-caption mr-2">Password Strength:</span>
                    <span class="text-caption font-weight-medium" :style="{ color: passwordStrengthColor }">
                      {{ passwordStrengthDescription }}
                    </span>
                  </div>
                  <v-progress-linear :value="passwordValidation.strength" :color="passwordStrengthColor" height="4"
                    rounded></v-progress-linear>
                </div>

                <!-- Password Validation Messages -->
                <div v-if="showPasswordValidation && newPassword.length > 0" class="mb-3">
                  <div v-if="passwordValidation.errors.length > 0" class="mb-2">
                    <div v-for="error in passwordValidation.errors" :key="error" class="text-caption text-error">
                      <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                      {{ error }}
                    </div>
                  </div>
                  <div v-if="passwordValidation.warnings.length > 0">
                    <div v-for="warning in passwordValidation.warnings" :key="warning"
                      class="text-caption text-warning">
                      <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
                      {{ warning }}
                    </div>
                  </div>
                </div>

                <v-text-field ref="confirmPassword" v-model="confirmPassword" class="mt-6"
                  :type="showConfirmPassword ? 'text' : 'password'" label="Confirm New Password" required dense outlined
                  prepend-inner-icon="lock_outline" :append-icon="showConfirmPassword ? 'visibility' : 'visibility_off'"
                  :hide-details="!showPasswordValidation || confirmPassword.length === 0" @input="validatePasswordMatch"
                  @click:append="showConfirmPassword = !showConfirmPassword">
                </v-text-field>

                <!-- Password Match Validation -->
                <div v-if="showPasswordValidation && confirmPassword.length > 0" class="mb-3">
                  <div v-if="newPassword !== confirmPassword" class="text-caption text-error">
                    <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                    Passwords do not match
                  </div>
                  <div v-else class="text-caption text-success">
                    <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                    Passwords match
                  </div>
                </div>
              </v-form>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions class="pa-4">
              <v-btn color="grey" outlined @click="goBackToLogin">Cancel</v-btn>
              <v-spacer></v-spacer>
              <v-btn color="primary" depressed :disabled="!valid || !canSubmit" :loading="loading"
                @click="resetPassword">
                Update Password
              </v-btn>
            </v-card-actions>
          </div>
          <p class="text-center custom-footer">
            © {{ new Date().getFullYear() }} All rights reserved by GoDial
          </p>
        </v-col>
      </v-row>
    </v-col>

    <v-dialog v-model="loaderDialog" hide-overlay persistent width="300">
      <v-card color="primary" dark>
        <v-card-text>
          Updating your password...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import PasswordSecurity from "../js/password-security.js";

export default {
  name: 'ForcePasswordReset',
  data() {
    return {
      valid: false,
      usernameOrEmail: "",
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      loading: false,
      loaderDialog: false,
      passwordValidation: {
        isValid: false,
        errors: [],
        warnings: [],
        strength: 0
      },
      showPasswordValidation: false,
      debounceTimer: null,

      usernameRules: [
        v => !!v || 'Username or email is required',
        v => (v && v.length >= 3) || 'Username must be at least 3 characters',
      ],
      currentPasswordRules: [
        v => !!v || 'Current password is required',
      ],
    };
  },
  computed: {
    ...mapGetters(["ENDPOINT"]),
    prefilledUsername() {
      return this.$route.query.username || '';
    },
    passwordStrengthColor() {
      return PasswordSecurity.getStrengthColor(this.passwordValidation.strength);
    },
    passwordStrengthDescription() {
      return PasswordSecurity.getStrengthDescription(this.passwordValidation.strength);
    },
    isPasswordValid() {
      return this.passwordValidation.isValid && this.newPassword === this.confirmPassword;
    },
    canSubmit() {
      return this.valid &&
        this.usernameOrEmail &&
        this.currentPassword &&
        this.newPassword &&
        this.confirmPassword &&
        this.isPasswordValid;
    }
  },
  watch: {
    newPassword(newVal) {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.validatePassword();
      }, 300);
    }
  },
  created() {
    // Pre-fill username if provided from login page
    if (this.prefilledUsername) {
      this.usernameOrEmail = this.prefilledUsername;
    }
  },
  methods: {
    // Validate password using PasswordSecurity utility
    validatePassword() {
      if (this.newPassword.length > 0) {
        this.passwordValidation = PasswordSecurity.validatePassword(this.newPassword);
        this.showPasswordValidation = true;
      } else {
        this.passwordValidation = {
          isValid: false,
          errors: [],
          warnings: [],
          strength: 0
        };
      }
    },

    // Validate password match
    validatePasswordMatch() {
      // This method is called when confirm password changes
      // The validation is handled in the computed property isPasswordValid
    },

    async resetPassword() {
      if (!this.$refs.resetForm.validate()) {
        return;
      }

      // Check password validation
      if (!this.passwordValidation.isValid) {
        this.$swal({
          type: "error",
          text: "Password does not meet security requirements. Please check the validation messages below.",
        });
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        this.$swal({
          type: "warning",
          text: "Passwords do not match.",
        });
        return;
      }

      if (!this.canSubmit) {
        this.$swal({
          type: "error",
          text: "Please ensure all fields are valid before submitting.",
        });
        return;
      }

      this.loading = true;
      this.loaderDialog = true;

      try {
        const response = await this.$http.post(
          `${this.ENDPOINT}/accounts/force-password-reset`,
          {
            usernameOrEmail: this.usernameOrEmail,
            currentPassword: this.currentPassword,
            newPassword: this.newPassword
          }
        );

        this.loaderDialog = false;
        this.loading = false;

        this.$swal({
          type: "success",
          title: "Password Updated Successfully",
          text: response.body.message || "Your password has been updated. You can now login with your new password.",
          showConfirmButton: true,
        }).then(() => {
          this.$router.replace({ path: "/" });
        });

      } catch (error) {
        this.loaderDialog = false;
        this.loading = false;

        console.error('Password reset error:', error);

        let errorMessage = "An error occurred while updating your password.";

        if (error.status === 401 && error.body.error.code === 'INVALID_CREDENTIALS') {
          errorMessage = "The combination of email/username and the current password does not exist";
        } else if (error.status === 401 && error.body.error.code === 'INVALID_CURRENT_PASSWORD') {
          errorMessage = "The combination of email/username and the current password does not exist";
        } else if (error.status === 422 && error.body.error.code === 'WEAK_PASSWORD') {
          errorMessage = error.body.error.message || "Password does not meet security requirements.";
        } else if (error.status === 422 && error.body.error.code === 'SAME_PASSWORD') {
          errorMessage = "New password must be different from your current password.";
        } else if (error.body && error.body.error && error.body.error.message) {
          errorMessage = error.body.error.message;
        }

        this.$swal({
          type: "error",
          text: errorMessage,
        });
      }
    },

    goBackToLogin() {
      this.$router.replace({ path: "/" });
    }
  },
};
</script>

<style scoped>
.custom-height {
  min-height: 100%;
}

.custom-container-height {
  min-height: 100vh;
  background-image: linear-gradient(to right, #37cf37, #37cf37);
}

.custom-footer {
  color: white;
  margin-top: 2rem;
  font-weight: 500;
  font-family: Poppins, sans-serif;
}

.card-style {
  background-color: white;
}

.rounded-card {
  border-radius: 12px;
}

.title-style {
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.text-error {
  color: #f44336 !important;
}

.text-warning {
  color: #ff9800 !important;
}

.text-success {
  color: #4caf50 !important;
}
</style>